import { Link, Navigate } from 'react-router';
import PageShell from '@components/pages/PageShell';
import Seo from '@components/Seo';
import { ArticleSkeleton, Skeleton } from '@components/Skeleton';
import { SITE_STATE } from '@/lib/siteContent';
import { ArticleCard, Cover, SectionLabel, type ArticleItem } from '@components/pages/ResourceSections';
import { absoluteUrl } from '@components/pages/ResourceActions';
import { SOCIAL_URLS } from '@/data/navigation';
import Newsletter from '@components/landing/Newsletter';
import JsonLd from '@components/JsonLd';
import { articleSchema, breadcrumbSchema } from '@/lib/schema';
import { Img } from '@/components/Img';

/** Paragraphs shown before the pull quote; the remainder follows it. */
const SPLIT_AFTER = 3;

/**
 * The icons beside an article, and where each one actually goes.
 *
 * Facebook and X both take a URL and open a composer, so those two genuinely
 * share the page being read. Instagram and YouTube have no such address: there
 * is no way to hand either of them a link, so those two go to the
 * organisation's own accounts and their labels say so rather than promising a
 * share that cannot happen. Every one of them used to be `href="#"`.
 */
const SHARE = [
    {
        name: 'Facebook',
        icon: '/landing/blog/facebook.svg',
        share: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
        name: 'X',
        icon: '/landing/blog/twitter.svg',
        share: (url: string, title: string) =>
            `https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    },
    { name: 'Instagram', icon: '/landing/blog/instagram.svg', network: 'instagram' },
    { name: 'YouTube', icon: '/landing/blog/youtube.svg', network: 'youtube' },
] as const;

/**
 * The long-form reading layout behind both the blog and the stories section:
 * headline, byline, hero image, body split by a pull quote, and a related rail.
 */
export default function ArticleDetail({
    item,
    related,
    basePath,
    backLabel,
    relatedLabel,
    kindLabel = 'article',
    meta,
    bodyPending = false,
}: {
    item?: ArticleItem;
    related: ArticleItem[];
    basePath: string;
    backLabel: string;
    relatedLabel: string;
    /** Singular noun used in the share prompt — "article" or "story". */
    kindLabel?: string;
    /** Overrides the date · reading-time line; the CMS values are used when it is omitted. */
    meta?: string;
    /** The body is still being fetched, so hold its space rather than closing the gap. */
    bodyPending?: boolean;
}) {
    // The feed can still be in flight — the app mounts on a timer, not on the
    // data. Concluding "no such article" before it lands sent every shared or
    // bookmarked link to the 404 page, and a redirect cannot be taken back once
    // the content arrives.
    if (!item) {
        if (SITE_STATE.hydrating) {
            return (
                <PageShell>
                    <div className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                        <ArticleSkeleton />
                    </div>
                </PageShell>
            );
        }
        return <Navigate to="/404" replace />;
    }

    const trail = [
        { name: 'Home', path: '/' },
        { name: relatedLabel.replace(/^(More|Related)\s+/i, ''), path: basePath },
        { name: item.title, path: `${basePath}/${item.slug}` },
    ];
    /** The same address the canonical tag names, so a share points at the real page. */
    const shareUrl = absoluteUrl(`${basePath}/${item.slug}`);
    const seo = (
        <Seo
            title={item.title}
            description={item.excerpt}
            image={item.image}
            imageAlt={item.title}
            canonical={`${basePath}/${item.slug}`}
            type="article"
            publishedTime={item.publishedAt}
            author={item.author}
            tags={item.tags}
        />
    );

    const body = item.body ?? [];
    const head = body.slice(0, SPLIT_AFTER);
    const tail = body.slice(SPLIT_AFTER);
    const metaLine =
        meta ?? [item.date, item.readingMinutes ? `${item.readingMinutes} min read` : ''].filter(Boolean).join(' · ');

    return (
        <PageShell>
            {seo}
            <JsonLd
                id="article"
                data={[
                    articleSchema(
                        {
                            title: item.title,
                            slug: item.slug,
                            excerpt: item.excerpt,
                            image: item.image,
                            author: item.author,
                            publishedAt: item.publishedAt,
                            tags: item.tags,
                        },
                        basePath,
                    ),
                    breadcrumbSchema(trail),
                ]}
            />
            <article className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <Link to={basePath} className="text-[13px] font-semibold text-[#5e5e5e] hover:text-[#f60]">
                        &larr; {backLabel}
                    </Link>

                    <h1 className="font-primary mt-5 max-w-[900px] text-[32px] font-bold leading-[1.2] tracking-[-1px] text-[#08080c] lg:text-[52px] lg:tracking-[-2px]">
                        {item.title}
                    </h1>

                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        <span className="flex items-center gap-2.5">
                            <Cover src={item.avatar} className="size-9 rounded-full object-cover" />
                            <span className="text-[13px] font-semibold text-[#08080c]">By {item.author}</span>
                        </span>
                        {metaLine && <span className="text-[13px] text-[#5e5e5e]">{metaLine}</span>}
                    </div>

                    <div className="mt-8 h-[220px] w-full overflow-hidden rounded-[14px] bg-[#d9d9d9] lg:h-[520px]">
                        <Cover src={item.image} fallback={item.fallbackImage} className="size-full object-cover" />
                    </div>

                    <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:gap-16">
                        <div className="min-w-0 lg:flex-1">
                            {!item.bodyHtml && !body.length ? (
                                // The listing feed carries no bodies; this one is still on
                                // its way from `/site/resources/:collection/:id`.
                                //
                                // The reserved height is the point. A shift only counts
                                // against the page when it moves something a visitor can
                                // see, and four short lines left the share column and the
                                // footer on screen, so the body's arrival shoved both down
                                // and scored 0.26 on its own. Holding more than a screen
                                // means they start below the fold and the real body lands
                                // out of sight.
                                bodyPending ? (
                                    <div
                                        className="flex min-h-[120vh] flex-col gap-4"
                                        role="status"
                                        aria-label="Loading"
                                    >
                                        {[96, 88, 93, 75, 91, 84, 97, 70, 89, 94, 79, 92].map((width, i) => (
                                            <Skeleton key={i} className="h-[12px]" style={{ width: `${width}%` }} />
                                        ))}
                                    </div>
                                ) : null
                            ) : item.bodyHtml ? (
                                // Sanitised by the API on every write, against the same allow-list the
                                // editor uses — headings, lists, quotes, links and images only.
                                <div className="article-body" dangerouslySetInnerHTML={{ __html: item.bodyHtml }} />
                            ) : (
                                <>
                                    {head.map((p, i) => (
                                        <p
                                            key={i}
                                            className="mb-5 text-[14px] leading-[1.9] text-[#333] lg:text-[15px]"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                    {item.quote && (
                                        <blockquote className="my-8 border-l-[3px] border-[#f60] pl-6 text-[18px] font-medium leading-[1.6] text-[#08080c] lg:text-[22px]">
                                            {item.quote}
                                        </blockquote>
                                    )}
                                    {tail.map((p, i) => (
                                        <p
                                            key={`b-${i}`}
                                            className="mb-5 text-[14px] leading-[1.9] text-[#333] lg:text-[15px]"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </>
                            )}

                            {(item.tags?.length || item.originalUrl) && (
                                <footer className="mt-10 flex flex-wrap items-center gap-2 border-t border-[#e0e0e0] pt-6">
                                    {item.tags?.map((t) => (
                                        <span
                                            key={t}
                                            className="rounded-full bg-[#f2f2f2] px-3 py-1 text-[12px] font-medium text-[#333]"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                    {item.originalUrl && (
                                        <a
                                            href={item.originalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-auto text-[12px] font-semibold text-[#5e5e5e] hover:text-[#f60]"
                                        >
                                            Originally published on Medium ↗
                                        </a>
                                    )}
                                </footer>
                            )}
                        </div>

                        <aside className="lg:w-[240px] lg:shrink-0">
                            <p className="text-[13px] font-bold text-[#08080c]">Share this {kindLabel}</p>
                            <div className="mt-4 flex gap-3">
                                {SHARE.map((s) => {
                                    const href = 'share' in s ? s.share(shareUrl, item.title) : SOCIAL_URLS[s.network];
                                    if (!href) return null;
                                    return (
                                        <a
                                            key={s.name}
                                            href={href}
                                            target="_blank"
                                            // Neither a share composer nor our own profile is an
                                            // editorial link, so crawlers are asked not to follow.
                                            rel="nofollow noopener noreferrer"
                                            aria-label={
                                                'share' in s
                                                    ? `Share this ${kindLabel} on ${s.name}`
                                                    : `Citizens’ Gavel on ${s.name}`
                                            }
                                            className="flex size-9 items-center justify-center rounded-full border border-[#e0e0e0]"
                                        >
                                            <Img src={s.icon} alt="" className="size-4" />
                                        </a>
                                    );
                                })}
                            </div>
                        </aside>
                    </div>
                </div>
            </article>

            <section className="bg-white pb-14 lg:pb-20">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <SectionLabel>{relatedLabel}</SectionLabel>
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((a) => (
                            <ArticleCard key={a.slug} item={a} basePath={basePath} />
                        ))}
                    </div>
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
